import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Box,
  Avatar,
  IconButton,
  InputAdornment,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  Storage as InventoryIcon,
} from "@material-ui/icons";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(3),
    minWidth: 600,
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  imageUpload: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    cursor: "pointer",
    transition: "border-color 0.3s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  imagePreview: {
    width: 120,
    height: 120,
    borderRadius: theme.spacing(1),
  },
  uploadText: {
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  priceField: {
    "& input": {
      textAlign: "right",
    },
  },
  stockField: {
    "& input": {
      textAlign: "center",
    },
  },
  switchContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 0),
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    zIndex: 1000,
  },
}));

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Nome é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
  description: Yup.string()
    .max(500, "Descrição deve ter no máximo 500 caracteres"),
  price: Yup.number()
    .required("Preço é obrigatório")
    .min(0.01, "Preço deve ser maior que zero")
    .max(999999.99, "Preço muito alto"),
  category: Yup.string()
    .required("Categoria é obrigatória"),
  stockQuantity: Yup.number()
    .required("Quantidade em estoque é obrigatória")
    .min(0, "Estoque não pode ser negativo")
    .integer("Estoque deve ser um número inteiro"),
});

const ProductForm = ({ open, onClose, product, onSave }) => {
  const classes = useStyles();
  
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (open) {
      loadCategories();
      if (product) {
        setImagePreview(product.imageUrl);
      } else {
        setImagePreview(null);
        setImageFile(null);
      }
    }
  }, [open, product]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get("/catalog/categories");
      setCategories(data.categories);
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toastUtils.error("Imagem deve ter no máximo 5MB");
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toastUtils.error("Arquivo deve ser uma imagem");
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const uploadImage = async (file) => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post('/catalog/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data.imageUrl;
    } catch (err) {
      console.error("Error uploading image:", err);
      throw err;
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      
      let imageUrl = product?.imageUrl || null;
      
      // Upload new image if selected
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }

      const productData = {
        ...values,
        price: Math.round(values.price * 100), // Convert to cents
        imageUrl,
      };

      if (product) {
        await api.put(`/catalog/products/${product.id}`, productData);
        toastUtils.success("Produto atualizado com sucesso!");
      } else {
        await api.post("/catalog/products", productData);
        toastUtils.success("Produto criado com sucesso!");
      }

      onSave();
    } catch (err) {
      console.error("Error saving product:", err);
      toastError(err);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const initialValues = {
    name: product?.name || "",
    description: product?.description || "",
    price: product ? product.price / 100 : 0, // Convert from cents
    category: product?.category || "",
    stockQuantity: product?.stockQuantity || 0,
    isActive: product?.isActive !== undefined ? product.isActive : true,
    isFeatured: product?.isFeatured || false,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {product ? "Editar Produto" : "Novo Produto"}
      </DialogTitle>
      
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form>
            <DialogContent className={classes.dialogContent}>
              {loading && (
                <div className={classes.loadingOverlay}>
                  <CircularProgress />
                </div>
              )}

              {/* Basic Information */}
              <div className={classes.formSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Informações Básicas
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Nome do Produto *"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      name="description"
                      label="Descrição"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && Boolean(errors.description)}
                      helperText={touched.description && errors.description}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={touched.category && Boolean(errors.category)}>
                      <InputLabel>Categoria *</InputLabel>
                      <Select
                        name="category"
                        value={values.category}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      >
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.name}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.category && errors.category && (
                        <Typography className={classes.errorText}>
                          {errors.category}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </div>

              {/* Image Upload */}
              <div className={classes.formSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Imagem do Produto
                </Typography>
                
                <Box className={classes.imageUpload}>
                  {imagePreview ? (
                    <Box position="relative">
                      <Avatar
                        src={imagePreview}
                        className={classes.imagePreview}
                        variant="rounded"
                      />
                      <IconButton
                        size="small"
                        onClick={handleRemoveImage}
                        style={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          backgroundColor: "white",
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <PhotoCameraIcon style={{ fontSize: 48, color: "#ccc" }} />
                      <Typography className={classes.uploadText}>
                        Clique para adicionar uma imagem
                        <br />
                        <small>Máximo 5MB - JPG, PNG</small>
                      </Typography>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                    id="image-upload"
                  />
                  <label htmlFor="image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                    >
                      {imagePreview ? "Alterar Imagem" : "Adicionar Imagem"}
                    </Button>
                  </label>
                </Box>
              </div>

              {/* Price and Stock */}
              <div className={classes.formSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Preço e Estoque
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="price"
                      label="Preço *"
                      type="number"
                      inputProps={{ 
                        step: "0.01",
                        min: "0"
                      }}
                      value={values.price}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.price && Boolean(errors.price)}
                      helperText={touched.price && errors.price}
                      className={classes.priceField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="stockQuantity"
                      label="Quantidade em Estoque *"
                      type="number"
                      inputProps={{ 
                        min: "0",
                        step: "1"
                      }}
                      value={values.stockQuantity}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.stockQuantity && Boolean(errors.stockQuantity)}
                      helperText={touched.stockQuantity && errors.stockQuantity}
                      className={classes.stockField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <InventoryIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </div>

              {/* Settings */}
              <div className={classes.formSection}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Configurações
                </Typography>
                
                <Box className={classes.switchContainer}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isActive"
                        checked={values.isActive}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="Produto Ativo"
                  />
                  <Typography variant="body2" color="textSecondary">
                    Produtos ativos aparecem no catálogo
                  </Typography>
                </Box>
                
                <Box className={classes.switchContainer}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="isFeatured"
                        checked={values.isFeatured}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="Produto em Destaque"
                  />
                  <Typography variant="body2" color="textSecondary">
                    Produtos em destaque aparecem primeiro
                  </Typography>
                </Box>
              </div>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting || loading}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? "Salvando..." : (product ? "Atualizar" : "Criar")}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ProductForm;